import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Constants} from '../constants/constants';
import {UtilsService} from './utils.service';
import {buildSquareAuthorizeUrl} from './square-oauth.util';

/**
 * Re-auth bridge invoked by `ErrorInterceptor` on a 401 from a partner API.
 *
 * Flow: clear the bad token in the DineNGo backend (so getAuthToken can't
 * hand it out again), then full-page redirect to the partner authorize URL.
 * The OAuth callback (existing Clover/Square auth controllers) writes a fresh
 * token and sends the user back into the app.
 */
@Injectable({providedIn: 'root'})
export class PartnerReauthService {
  private static readonly INFLIGHT_KEY = 'partnerReauthInFlight';

  constructor(private http: HttpClient, private utilsService: UtilsService) {}

  /**
   * True when the current user's org has a partner account that we can
   * actually re-authenticate against. Without this guard a stray 401 from
   * an unrelated request would bounce a non-POS user off to Clover OAuth.
   */
  hasConnectedPartner(): boolean {
    const user = this.utilsService.getCurrentUser();
    const accountType = user?.orgSettings?.accountType;
    return accountType === Constants.CLOVER || accountType === Constants.SQUARE;
  }

  /**
   * Returns 'Clover' | 'Square' | null. Resolved from the merchant's
   * configured `accountType` rather than from the failing request URL —
   * Clover orgs may still hit Square endpoints during partial migrations
   * and we'd rather re-auth the platform the merchant actually uses.
   */
  detectPlatform(): 'Clover' | 'Square' | null {
    const user = this.utilsService.getCurrentUser();
    const accountType = user?.orgSettings?.accountType;
    if (accountType === Constants.CLOVER) { return 'Clover'; }
    if (accountType === Constants.SQUARE) { return 'Square'; }
    return null;
  }

  /**
   * Try-refresh-then-skip-redirect flow on a partner-API 401. Idempotent
   * within a tab via a sessionStorage flag so concurrent 401s don't pile
   * up. Returns true when handling was accepted (caller stops error
   * propagation in either case); false when the caller should fall
   * through to its normal error handling.
   *
   * The backend's `clearPartnerToken` is refresh-only — it never wipes the
   * credential row anymore. Possible responses:
   *   { cleared: false, refreshed: true  } — refresh succeeded, fresh
   *                                          token persisted; next call
   *                                          works.
   *   { cleared: false, refreshed: false } — refresh failed (refresh
   *                                          token itself expired or
   *                                          revoked). Row is left intact;
   *                                          the failing request errors
   *                                          out; the merchant uses the
   *                                          manual "Reconnect Clover"
   *                                          button on the settings page
   *                                          if a genuine revocation
   *                                          persists.
   *
   * Either way the FE no longer auto-bounces to partner OAuth on a 401.
   * The pre-2026-04-27 behavior (one OAuth per merchant, refresh handles
   * everything else for years) is what this restores.
   *
   * The `redirect` closure below is retained only as a safety net for
   * network failure on the `clearPartnerToken` call itself, where the
   * server-side outcome is unknown — falling back to OAuth there is
   * preferable to leaving the cashier on a stale page with no recovery
   * path. The `resp.cleared === true` check is a forward-compat guard
   * against a future server-side rollback that re-introduces the wipe
   * path.
   */
  triggerReauth(): boolean {
    const platform = this.detectPlatform();
    if (!platform) { return false; }

    try {
      if (sessionStorage.getItem(PartnerReauthService.INFLIGHT_KEY) === platform) {
        return true;
      }
      sessionStorage.setItem(PartnerReauthService.INFLIGHT_KEY, platform);
    } catch {
      // sessionStorage can throw in private-mode Safari; we still want the
      // call to fire, we just lose dedupe.
    }

    const redirect = () => {
      try {
        if (platform === 'Square') {
          window.location.assign(buildSquareAuthorizeUrl());
        } else {
          window.location.assign(`${environment.cloverAuthUrl}?client_id=${environment.client_id}`);
        }
      } catch {
        // Surface as a plain reload as a last resort.
        window.location.reload();
      }
    };

    const skipRedirect = () => {
      // Whether the BE refreshed or just couldn't, we don't redirect.
      // Clear the inflight flag so the next genuine 401 (if any) can
      // re-trigger.
      PartnerReauthService.clearInflight();
    };

    this.http.post(`${environment.apiUrl}/clientCredentials/clearPartnerToken`, {platform})
      .subscribe(
        (resp: any) => {
          if (resp && resp.cleared === true) {
            redirect();
          } else {
            skipRedirect();
          }
        },
        (err: any) => {
          // #1080 fix — server-restart no-bounce.
          //
          // Pre-fix, ANY error from clearPartnerToken bounced the cashier
          // to Clover OAuth. After a backend restart, the very first
          // authenticated call from an open tab can briefly return 401 /
          // 502 / 504 — clearPartnerToken inherits that failure mode,
          // and the merchant saw the Clover auth page on every restart
          // even though their access + refresh tokens were perfectly
          // fine in Mongo.
          //
          // Only redirect to OAuth on a TRUE network-level failure (no
          // HTTP response at all). Any HTTP-status-bearing error means
          // the server is up enough to answer — partner re-auth is
          // almost certainly NOT what the merchant needs. We clear the
          // inflight flag and let the original 401 propagate through
          // the normal ErrorInterceptor path (which itself only logs
          // out for non-partner 401s, and for partner-401s lets the
          // caller surface its own error).
          //
          // Net effect: a Mongo/Clover blip no longer drags every open
          // device to Clover OAuth. The merchant's saved Clover
          // credentials stay valid across restarts.
          const isNetworkOnly = !err || (err.status === 0 || err.status == null);
          if (isNetworkOnly) {
            redirect();
          } else {
            console.warn(
              '[PartnerReauth] clearPartnerToken errored with HTTP ' +
              (err.status || 'unknown') + ' — skipping Clover OAuth bounce. ' +
              'Original partner-401 will propagate normally.',
            );
            skipRedirect();
          }
        },
      );

    return true;
  }

  /** Test-only / route-guard hook to clear the inflight flag on success. */
  static clearInflight(): void {
    try { sessionStorage.removeItem(PartnerReauthService.INFLIGHT_KEY); } catch { /* noop */ }
  }
}
